import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  SankeyChartViewModel,
  SankeyFlowGradient,
  SankeyFlowView,
  SankeyStageInput,
  SankeyStageView,
} from './sankey-chart.models';

const DEFAULT_LAYOUT = {
  chartWidth: 900,
  chartHeight: 340,
  barWidth: 10,
  maxBarHeight: 310,
  minBarRatio: 0.25,
  barPaddingLeft: 8,
};

@Component({
  selector: 'app-sankey-chart',
  templateUrl: './sankey-chart.component.html',
  styleUrls: ['./sankey-chart.component.scss'],
})
export class SankeyChartComponent implements OnChanges {
  /** Array de estágios (mínimo 2) com label, count, percentOfTotal e cor */
  @Input() stages: SankeyStageInput[] = [];

  /** Texto exibido quando não há dados */
  @Input() emptyMessage = 'Sem dados para exibir.';

  /** Chaves dos filtros ativos — destaca etapas selecionadas */
  @Input() activeFilterKeys: string[] = [];

  @Output() stageSelect = new EventEmitter<SankeyStageInput>();

  public viewModel: SankeyChartViewModel | null = null;
  public flowGradients: SankeyFlowGradient[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stages) {
      this.buildChart();
    }
  }

  private buildChart(): void {
    if (!this.stages || this.stages.length < 2 || this.stages[0].count <= 0) {
      this.viewModel = null;
      this.flowGradients = [];
      return;
    }

    const { chartWidth, chartHeight, barWidth, maxBarHeight, minBarRatio, barPaddingLeft } = DEFAULT_LAYOUT;
    const columnCount = this.stages.length;
    const colWidth = chartWidth / columnCount;

    const maxCount = this.stages[0].count || 1;

    // Gerar gradientes para os fluxos entre estágios
    this.flowGradients = this.stages.slice(0, -1).map((stage, i) => ({
      id: `sankey-flow-grad-${i}`,
      from: stage.color,
      to: this.stages[i + 1].color,
    }));

    // Gerar dividers (linhas verticais entre colunas)
    const dividerPositions: number[] = [];
    for (let i = 1; i < columnCount; i++) {
      dividerPositions.push(colWidth * i);
    }

    // Calcular barras
    const stageViews: SankeyStageView[] = this.stages.map((stage, index) => {
      const count = stage.count;

      let barHeight: number;
      if (count === 0) {
        barHeight = 0;
      } else {
        const ratio = count / maxCount;
        const sqrtRatio = Math.sqrt(ratio);
        const scaledRatio = minBarRatio + sqrtRatio * (1 - minBarRatio);
        barHeight = Math.max(scaledRatio * maxBarHeight, 12);
      }

      const barX = index === 0 ? barPaddingLeft : index * colWidth - barWidth;
      const centerY = chartHeight / 2;
      const barY = centerY - barHeight / 2;

      return {
        label: stage.label,
        count: stage.count,
        percentOfTotal: stage.percentOfTotal,
        barColor: stage.color,
        filterKey: stage.filterKey,
        barX,
        barY,
        barHeight,
      };
    });

    // Calcular flows (paths curvos entre barras)
    const flows: SankeyFlowView[] = [];
    for (let i = 0; i < stageViews.length - 1; i++) {
      const from = stageViews[i];
      const to = stageViews[i + 1];

      const x1 = from.barX + barWidth;
      const x2 = to.barX;
      const midX = (x1 + x2) / 2;

      const y1Top = from.barY;
      const y1Bottom = from.barY + from.barHeight;
      const y2Top = to.barY;
      const y2Bottom = to.barY + to.barHeight;

      const path = [
        `M ${x1} ${y1Top}`,
        `C ${midX} ${y1Top}, ${midX} ${y2Top}, ${x2} ${y2Top}`,
        `L ${x2} ${y2Bottom}`,
        `C ${midX} ${y2Bottom}, ${midX} ${y1Bottom}, ${x1} ${y1Bottom}`,
        'Z',
      ].join(' ');

      flows.push({ path, gradientId: this.flowGradients[i].id });
    }

    this.viewModel = {
      stages: stageViews,
      flows,
      chartWidth,
      chartHeight,
      barWidth,
      dividerPositions,
    };
  }

  onStageClick(stageIndex: number): void {
    const stage = this.stages?.[stageIndex];
    if (stage?.filterKey) {
      this.stageSelect.emit(stage);
    }
  }

  isStageActive(stage: SankeyStageView): boolean {
    return !!stage.filterKey && this.activeFilterKeys.includes(stage.filterKey);
  }

  isStageDimmed(stage: SankeyStageView): boolean {
    return this.activeFilterKeys.length > 0 && !this.isStageActive(stage);
  }
}
